import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import he from './locales/he.json';
import en from './locales/en.json';
import ar from './locales/ar.json';
import fr from './locales/fr.json';
import ru from './locales/ru.json';

export const SUPPORTED_LANGUAGES = {
  he: { name: 'עברית', dir: 'rtl', flag: '🇮🇱' },
  en: { name: 'English', dir: 'ltr', flag: '🇺🇸' },
  ar: { name: 'العربية', dir: 'rtl', flag: '🇸🇦' },
  fr: { name: 'Français', dir: 'ltr', flag: '🇫🇷' },
  ru: { name: 'Русский', dir: 'ltr', flag: '🇷🇺' },
} as const;

export type SupportedLanguage = keyof typeof SUPPORTED_LANGUAGES;

export const RTL_LANGUAGES: SupportedLanguage[] = ['he', 'ar'];

export function isRTL(lang: SupportedLanguage): boolean {
  return RTL_LANGUAGES.includes(lang);
}

export function getDirection(lang: SupportedLanguage): 'rtl' | 'ltr' {
  return isRTL(lang) ? 'rtl' : 'ltr';
}

const resources = {
  he: { translation: he },
  en: { translation: en },
  ar: { translation: ar },
  fr: { translation: fr },
  ru: { translation: ru },
};

// Get saved language or browser language or default to Hebrew
const getSavedLanguage = (): SupportedLanguage => {
  const saved = localStorage.getItem('language');
  if (saved && saved in SUPPORTED_LANGUAGES) {
    return saved as SupportedLanguage;
  }

  const browserLang = navigator.language.split('-')[0];
  if (browserLang in SUPPORTED_LANGUAGES) {
    return browserLang as SupportedLanguage;
  }

  return 'he';
};

i18n.use(initReactI18next).init({
  resources,
  lng: getSavedLanguage(),
  fallbackLng: 'he',
  interpolation: {
    escapeValue: false,
  },
  react: {
    useSuspense: false,
  },
});

// Update document direction and language on language change
i18n.on('languageChanged', (lng) => {
  const lang = lng as SupportedLanguage;
  document.documentElement.lang = lang;
  document.documentElement.dir = getDirection(lang);
  localStorage.setItem('language', lang);
});

// Set initial direction
document.documentElement.dir = getDirection(getSavedLanguage());

export default i18n;
